import {useEffect, useRef} from 'react'
import {overwriteSavedFeeds} from '@bsky/sdk'
import {useQueryClient} from '@tanstack/react-query'

import {BLACKSKY_TREND_FEED_URI, DISCOVER_FEED_URI} from '#/lib/constants'
import {logger} from '#/logger'
import * as persisted from '#/state/persisted'
import {
  preferencesQueryKey,
  usePreferencesQuery,
} from '#/state/queries/preferences'
import {usePdsClient, useSession} from '#/state/session'

function markMigrated(did: string) {
  const migrated = persisted.get('migratedTrendingFeedDids') ?? []
  if (migrated.includes(did)) return
  void persisted.write('migratedTrendingFeedDids', [...migrated, did])
}

/**
 * One-shot per-DID migration: rewrite pinned Blacksky trending to the
 * GrapeVyne-owned `trending` generator. Keeps saved-feed id, pin, and order.
 * Mount before CultureFeedMigration so Culture can find Discover by the new URI.
 */
export function TrendingFeedMigration() {
  const {currentAccount} = useSession()
  const pdsClient = usePdsClient()
  const queryClient = useQueryClient()
  const {data: preferences} = usePreferencesQuery()
  const inFlight = useRef(false)

  useEffect(() => {
    const did = currentAccount?.did
    if (!did || !preferences) return

    const migrated = persisted.get('migratedTrendingFeedDids') ?? []
    if (migrated.includes(did)) return

    const hasBlackskyTrend = preferences.savedFeeds.some(
      f => f.value === BLACKSKY_TREND_FEED_URI,
    )
    if (!hasBlackskyTrend) {
      markMigrated(did)
      return
    }

    if (inFlight.current) return
    inFlight.current = true

    const migrate = async () => {
      try {
        const next = preferences.savedFeeds.map(f =>
          f.value === BLACKSKY_TREND_FEED_URI
            ? {...f, value: DISCOVER_FEED_URI}
            : f,
        )
        await pdsClient.call(overwriteSavedFeeds, next)
        await queryClient.invalidateQueries({
          queryKey: preferencesQueryKey,
        })
        markMigrated(did)
      } catch (e) {
        logger.error('trending feed migration failed', {safeMessage: e})
      } finally {
        inFlight.current = false
      }
    }

    void migrate()
  }, [pdsClient, currentAccount?.did, preferences, queryClient])

  return null
}
